import { useState, useMemo, useCallback, useEffect } from 'react';
import type { ReleaseInput, ReleaseDecision } from './types';
import { evaluateRelease, APP_ENV } from './api';
import ReleaseForm from './components/ReleaseForm';
import ResultBoard from './components/ResultBoard';
import Dashboard from './components/Dashboard';
import HomePage from './components/HomePage';
import EvaluationWizard from './components/EvaluationWizard';
import TestcaseManagerPage from './components/TestcaseManagerPage';
import FlowTestcaseGeneratorPage from './components/FlowTestcaseGeneratorPage';
import LoginButton from './components/LoginButton';
import UserMenu from './components/UserMenu';
import PricingModal from './components/PricingModal';
import ProfileModal from './components/ProfileModal';
import HistoryModal from './components/HistoryModal';
import HistoryDetailModal from './components/HistoryDetailModal';
import StatisticsModal from './components/StatisticsModal';
import TestcaseAnalytics from './components/TestcaseAnalytics';
import CriteriaSettingsModal from './components/CriteriaSettingsModal';
import TeamDashboardModal from './components/TeamDashboardModal';
import OnboardingModal from './components/OnboardingModal';
import TermsModal from './components/TermsModal';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import JoinTeamPage from './components/JoinTeamPage';
import HistoryPage from './components/HistoryPage';
import ApiUsageBanner from './components/ApiUsageBanner';
import BetaEndingBanner from './components/BetaEndingBanner';
import ErrorPage from './components/ErrorPage';
import LogoAnimation from './components/LogoAnimation';
import EnvironmentBadge from './components/EnvironmentBadge';
import DevPlanSwitcher from './components/DevPlanSwitcher';
import InlineDashboard from './components/InlineDashboard';
import AutoLogoutWarning, { LoggedOutModal } from './components/AutoLogoutWarning';
import FarewellModal from './components/FarewellModal';
import { useAutoLogout } from './auth/useAutoLogout';
import { useBodyScrollLock } from './hooks';
// QuotaDisplay는 이제 헤더에 통합됨
// import QuotaDisplay from './components/QuotaDisplay';
import { useLanguage, formatDate } from './i18n';
import { useAuth } from './auth';
import { analytics } from './analytics';
import { getDemoInput, getDemoResult } from './demoData';

// 베타 종료 예정일 (KST, 변경 시 이 값만 수정)
// 베타 무료 혜택: ~2026.02.28 / 2026.03.01부터 Free 플랜 전환
const BETA_END_DATE = '2026-02-28';
// 개발 예정 기능 플래그: local/staging 환경에서만 활성화
const ENABLE_TESTCASE_MANAGER = APP_ENV === 'local' || APP_ENV === 'staging';
const DELETE_FAREWELL_KEY = 'releasegate_show_delete_farewell';
const DELETE_FAREWELL_PAYLOAD_KEY = 'releasegate_delete_farewell_payload';
const MERGE_NOTICE_KEY = 'releasegate_merge_logout_notice';

// 테스팅 명언 모음 (KO/EN)
const TESTING_QUOTES = [
  { 
    ko: "품질은 행동이 아니라 습관이다.",
    en: "Quality is not an act, it is a habit.",
    author: "Aristotle"
  },
  { 
    ko: "테스트는 실패로 이끌고, 실패는 이해로 이끈다.",
    en: "Testing leads to failure, and failure leads to understanding.",
    author: "Burt Rutan"
  },
  { 
    ko: "당신이 제품 테스트를 싫어한다면, 고객도 그럴 것이다.",
    en: "If you don't like testing your product, most likely your customers won't like to test it either.",
    author: "Unknown"
  },
  { 
    ko: "저렴한 가격의 달콤함이 사라진 후에도, 낮은 품질의 쓴맛은 오래 남는다.",
    en: "The bitterness of poor quality remains long after the sweetness of low price is forgotten.",
    author: "Benjamin Franklin"
  },
  { 
    ko: "버그가 아니라, 문서화되지 않은 기능이다.",
    en: "It's not a bug, it's an undocumented feature.",
    author: "Anonymous"
  },
  { 
    ko: "먼저 문제를 해결하라. 그 다음에 코드를 작성하라.",
    en: "First, solve the problem. Then, write the code.",
    author: "John Johnson"
  },
  { 
    ko: "소프트웨어가 재사용 가능하려면, 먼저 사용 가능해야 한다.",
    en: "Before software can be reusable it first has to be usable.",
    author: "Ralph Johnson"
  },
  { 
    ko: "프로그램의 가장 중요한 속성은 사용자의 의도를 달성하는지 여부다.",
    en: "The most important property of a program is whether it accomplishes the intention of its user.",
    author: "C.A.R. Hoare"
  },
  { 
    ko: "프로그램 테스트는 버그의 존재를 보여줄 수 있지만, 부재를 증명할 수는 없다.",
    en: "Program testing can be used to show the presence of bugs, but never to show their absence.",
    author: "Edsger Dijkstra"
  },
  { 
    ko: "디버깅은 코드 작성보다 두 배 어렵다.",
    en: "Debugging is twice as hard as writing the code in the first place.",
    author: "Brian Kernighan"
  },
  { 
    ko: "품질은 공짜지만, 기꺼이 비용을 지불하는 자에게만 주어진다.",
    en: "Quality is free, but only to those who are willing to pay heavily for it.",
    author: "Tom DeMarco"
  },
  { 
    ko: "디버깅이 버그를 제거하는 과정이라면, 프로그래밍은 버그를 넣는 과정이다.",
    en: "If debugging is the process of removing bugs, then programming must be the process of putting them in.",
    author: "Edsger Dijkstra"
  },
  { 
    ko: "코딩을 빨리 시작할수록, 완성은 더 늦어진다.",
    en: "The sooner you start coding, the longer it takes.",
    author: "Roy Carlson"
  },
  { 
    ko: "좋은 테스트는 확신을 주고, 나쁜 테스트는 거짓 확신을 준다.",
    en: "A good test gives you confidence, a bad test gives you false confidence.",
    author: "Unknown"
  },
  { 
    ko: "테스트는 버그를 찾는 것이 아니라, 리스크를 줄이는 것이다.",
    en: "Testing is not about finding bugs, it's about reducing risk.",
    author: "James Bach"
  },
  { 
    ko: "품질이란 아무도 보지 않을 때도 제대로 하는 것이다.",
    en: "Quality means doing it right when no one is looking.",
    author: "Henry Ford"
  },
  { 
    ko: "예방이 치료보다 낫다.",
    en: "Prevention is better than cure.",
    author: "Desiderius Erasmus"
  },
  { 
    ko: "이론적으로 이론과 실제는 같다. 실제로는 다르다.",
    en: "In theory, there is no difference between theory and practice. In practice, there is.",
    author: "Yogi Berra"
  },
  { 
    ko: "빠르게 가는 유일한 방법은 제대로 가는 것이다.",
    en: "The only way to go fast is to go well.",
    author: "Robert C. Martin"
  },
  { 
    ko: "먼저 동작하게, 그 다음 올바르게, 마지막으로 빠르게.",
    en: "Make it work, make it right, make it fast.",
    author: "Kent Beck"
  },
];

function App() {
  const { lang, t } = useLanguage();
  const { isAuthenticated, refreshQuota, quota, logout, githubPreauth, clearGithubPreauth, completeGithubRegister, isLoggingIn, isLoading: isAuthLoading } = useAuth();
  const [isGithubRegistering, setIsGithubRegistering] = useState(false);

  // 자동 로그아웃 (30분 비활동 시)
  const autoLogout = useAutoLogout({
    onLogout: logout,
    isAuthenticated,
    enabled: true,
  });
  const [result, setResult] = useState<ReleaseDecision | null>(null);
  const [inputData, setInputData] = useState<ReleaseInput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [prefillData, setPrefillData] = useState<ReleaseInput | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isDashboardExpanded, setIsDashboardExpanded] = useState(false); // 헤더 플랜 row 펼침 상태
  const [showHomePage, setShowHomePage] = useState(true); // 새로운 홈페이지 표시 상태
  const [showEvaluationWizard, setShowEvaluationWizard] = useState(false); // 스텝 위자드 모드
  // 위자드 모드 활성화 여부 (향후 사용자 설정에서 토글 가능하도록 확장 예정)
  const useWizardMode = true;
  const [isDemoResult, setIsDemoResult] = useState(false);
  const [showDemoSignupPopup, setShowDemoSignupPopup] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showWelcomeOnboarding, setShowWelcomeOnboarding] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [dontAskTodayChecked, setDontAskTodayChecked] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isBetaDdayInfoOpen, setIsBetaDdayInfoOpen] = useState(false);
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);
  const [isTestcaseAnalyticsOpen, setIsTestcaseAnalyticsOpen] = useState(false);
  const [isCriteriaSettingsOpen, setIsCriteriaSettingsOpen] = useState(false);
  const [isTeamDashboardOpen, setIsTeamDashboardOpen] = useState(false);
  const [showFarewellModal, setShowFarewellModal] = useState(false);
  const [farewellPayload, setFarewellPayload] = useState<{ created_at: string | null; name: string | null; email: string | null } | null>(null);
  const [showMergeLogoutNotice, setShowMergeLogoutNotice] = useState(false);
  const [mergeLogoutNoticeEmail, setMergeLogoutNoticeEmail] = useState<string | null>(null);

  // 단순 인라인 모달(D-day 안내 등)에서 배경 스크롤 방지
  useBodyScrollLock(isBetaDdayInfoOpen);

  // 탈퇴 완료 후 "아쉽다" 모달 표시 (로그아웃 이후에도 뜨도록 localStorage 플래그 사용)
  useEffect(() => {
    try {
      const v = localStorage.getItem(DELETE_FAREWELL_KEY);
      if (v) {
        localStorage.removeItem(DELETE_FAREWELL_KEY);
        const payloadRaw = localStorage.getItem(DELETE_FAREWELL_PAYLOAD_KEY);
        if (payloadRaw) {
          try {
            setFarewellPayload(JSON.parse(payloadRaw));
          } catch {
            setFarewellPayload(null);
          }
          localStorage.removeItem(DELETE_FAREWELL_PAYLOAD_KEY);
        } else {
          setFarewellPayload(null);
        }
        setShowFarewellModal(true);
      }
    } catch {
      // ignore
    }
  }, [isAuthenticated]);

  // 계정 통합 완료 후: 보안을 위해 로그아웃 안내 모달 표시
  useEffect(() => {
    try {
      const raw = localStorage.getItem(MERGE_NOTICE_KEY);
      if (raw) {
        localStorage.removeItem(MERGE_NOTICE_KEY);
        try {
          const payload = JSON.parse(raw);
          setMergeLogoutNoticeEmail(payload?.kept_user_email ?? null);
        } catch {
          setMergeLogoutNoticeEmail(null);
        }
        setShowMergeLogoutNotice(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // "오늘 하루 묻지 않기"가 활성화되어 있는지 확인
  const isDontAskTodayActive = useCallback(() => {
    const savedDate = localStorage.getItem('releasegate_dont_ask_unsaved_until');
    if (!savedDate) return false;
    
    const savedTime = new Date(savedDate).getTime();
    const now = new Date().getTime();
    
    // 저장된 시간이 현재 시간보다 미래면 (아직 자정 전이면) 활성화
    return savedTime > now;
  }, []);

  // URL 경로 상태 관리 (popstate 이벤트 감지용)
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // OAuth 콜백 등으로 전체 리로드가 일어난 뒤에도, 특정 모달을 복구하기 위한 쿼리 처리
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const shouldOpenProfile = url.searchParams.get('profile') === 'open';
      if (shouldOpenProfile) {
        setIsProfileOpen(true);
        // 한 번 열고 나면 URL은 깨끗하게 정리 (새로고침/뒤로가기 시 반복 오픈 방지)
        url.searchParams.delete('profile');
        url.searchParams.delete('github'); // github=connected 같은 플래그도 정리
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      // ignore
    }
  }, []);

  // query 파라미터가 사라지거나 edge 케이스가 있어도, 로컬스토리지 플래그로 프로필 모달 복구
  useEffect(() => {
    try {
      const v = localStorage.getItem('releasegate_open_profile');
      if (v) {
        localStorage.removeItem('releasegate_open_profile');
        setIsProfileOpen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // popstate 이벤트 리스닝 (뒤로가기/앞으로가기 + pushState 시 리렌더링)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      setCurrentPath(path);
      
      // URL 경로에 따라 앱 내부 상태 동기화
      if (path === '/') {
        // 홈으로 돌아갈 때 상태 초기화
        setShowHomePage(true);
        setShowDashboard(false);
        setShowEvaluationWizard(false);
        // 결과도 초기화 (새로운 평가 시작 가능하도록)
        setResult(null);
        setInputData(null);
        setError(null);
        setPrefillData(null);
        setIsDemoResult(false);
      } else if (path === '/dashboard') {
        setShowHomePage(false);
        setShowDashboard(true);
        setShowEvaluationWizard(false);
      } else if (path === '/evaluate') {
        setShowHomePage(false);
        setShowDashboard(false);
        setShowEvaluationWizard(useWizardMode);
      }
      // /history, /testcases 등은 별도 페이지로 렌더링되므로 처리 불필요
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // URL 경로 기반 라우팅 확인
  const joinInviteToken = useMemo(() => {
    const match = currentPath.match(/^\/join\/(.+)$/);
    return match ? match[1] : null;
  }, [currentPath]);

  // 에러 페이지 경로 확인
  const errorPageCode = useMemo(() => {
    const errorMatch = currentPath.match(/^\/error\/(401|402|404|429|500|505)$/);
    return errorMatch ? parseInt(errorMatch[1]) as 401 | 402 | 404 | 429 | 500 | 505 : null;
  }, [currentPath]);

  // 히스토리 페이지 경로 확인
  const isHistoryPage = useMemo(() => {
    return currentPath === '/history';
  }, [currentPath]);

  // 테스트케이스 관리 페이지 경로 확인
  const isTestcaseManagerPage = useMemo(() => {
    return currentPath === '/testcases';
  }, [currentPath]);

  // Flow Testcase Generator 페이지 경로 확인
  const isFlowGeneratorPage = useMemo(() => {
    return currentPath === '/flow-generator';
  }, [currentPath]);

  // 개발 예정 기능 접근 차단: /testcases 는 홈으로 리다이렉트
  useEffect(() => {
    if (!ENABLE_TESTCASE_MANAGER && window.location.pathname === '/testcases') {
      window.history.replaceState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [currentPath]);

  // 로그인 후 대기 중인 초대가 있으면 초대 페이지로 이동
  useEffect(() => {
    if (isAuthenticated) {
      const pendingToken = localStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        localStorage.removeItem('pendingInviteToken');
        window.location.href = `/join/${pendingToken}`;
      }
    }
  }, [isAuthenticated]);

  // 온보딩: 최초 진입 시 표시
  // 1. Welcome Onboarding: 완전 최초 접근자 (비로그인 + 쿠키/로컬스토리지 없음)
  // 2. Feature Onboarding: Welcome 완료 후 진입 시
  useEffect(() => {
    // 로그인된 사용자는 Welcome 온보딩 건너뛰기
    if (isAuthenticated) {
      localStorage.setItem('releasegate_welcome_seen', 'true');
      return;
    }
    
    const hasSeenWelcome = localStorage.getItem('releasegate_welcome_seen');
    const hasSeenOnboarding = localStorage.getItem('releasegate_onboarding_seen');
    
    if (!hasSeenWelcome) {
      // 완전 최초 접근자 (비로그인): Welcome 온보딩 표시
      setShowWelcomeOnboarding(true);
    } else if (!hasSeenOnboarding) {
      // Welcome은 봤지만 Feature 온보딩은 안 본 경우
      setShowOnboarding(true);
    }
  }, [isAuthenticated]);

  const handleWelcomeComplete = useCallback(() => {
    setShowWelcomeOnboarding(false);
    localStorage.setItem('releasegate_welcome_seen', 'true');
    // Welcome 후 Feature 온보딩(4단계 가이드)을 보여줌
    const hasSeenOnboarding = localStorage.getItem('releasegate_onboarding_seen');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, []);

  const handleOnboardingClose = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem('releasegate_onboarding_seen', 'true');
  }, []);

  // 네비게이션 가드: 폼 작성 중(결과가 없을 때)에만 변경사항 확인
  const navigateWithGuard = useCallback((action: () => void) => {
    // 결과 화면이면 바로 이동 (폼 작성 중이 아님)
    if (result) {
      action();
      return;
    }
    
    // "오늘 하루 묻지 않기"가 활성화되어 있으면 바로 이동
    if (isDontAskTodayActive()) {
      action();
      return;
    }
    
    // 폼 작성 중이고 변경사항이 있으면 확인 팝업 표시
    if (hasUnsavedChanges) {
      setPendingNavigation(() => action);
    } else {
      action();
    }
  }, [result, hasUnsavedChanges, isDontAskTodayActive]);

  // 확인 팝업에서 "나가기" 선택 시
  const handleConfirmLeave = useCallback(() => {
    if (pendingNavigation) {
      // "오늘 하루 묻지 않기" 체크되어 있으면 자정까지 저장
      if (dontAskTodayChecked) {
        const now = new Date();
        const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
        localStorage.setItem('releasegate_dont_ask_unsaved_until', midnight.toISOString());
      }
      
      setHasUnsavedChanges(false); // 상태 리셋
      setDontAskTodayChecked(false); // 체크박스 리셋
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, dontAskTodayChecked]);

  // 확인 팝업에서 "계속 작성" 선택 시
  const handleCancelLeave = useCallback(() => {
    setPendingNavigation(null);
    setDontAskTodayChecked(false); // 체크박스 리셋
  }, []);

  // 비로그인 시 데모 데이터로 prefill
  const isDemo = !isAuthenticated;
  const demoInput = useMemo(() => getDemoInput(lang), [lang]);
  const demoResult = useMemo(() => getDemoResult(lang), [lang]);

  // 언어 변경 시 데모 결과도 업데이트 (Sample 접두사 유지)
  useEffect(() => {
    if (isDemoResult && result) {
      const newDemoResult = getDemoResult(lang);
      const newDemoInput = getDemoInput(lang);
      setResult({
        ...newDemoResult,
        feature_name: `[Sample] ${newDemoResult.feature_name}`,
      });
      setInputData({
        ...newDemoInput,
        feature_name: `[Sample] ${newDemoInput.feature_name}`,
      });
    }
  }, [lang, isDemoResult, result]);

  // 데모 결과 5초 후 회원가입 팝업 표시
  useEffect(() => {
    if (isDemoResult && !isAuthenticated) {
      const timer = setTimeout(() => {
        setShowDemoSignupPopup(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isDemoResult, isAuthenticated]);
  // 참고: 언어 변경 시 재평가하지 않음
  // 평가 결과(status, score)는 동일하게 유지하고 UI 번역만 변경됨
  // ResultBoard에서 t.status.xxx를 사용하므로 자동으로 번역됨

  const handleSubmit = async (input: ReleaseInput) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const decision = await evaluateRelease(input);
      setInputData(input);
      setResult(decision);
      
      // GA4: 평가 성공 이벤트
      analytics.evaluateSuccess(decision.status, decision.confidence_score);
      
      // 스크롤 상단으로 이동
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // 평가 후 quota 새로고침
      await refreshQuota();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t.error.unknown;
      setError(errorMessage);
      
      // GA4: 평가 실패 이벤트
      analytics.evaluateError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 평가 시작 핸들러
  const handleStartEvaluation = useCallback(() => {
    setShowHomePage(false);
    setShowDashboard(false);
    if (useWizardMode) {
      setShowEvaluationWizard(true);
    } else {
      setShowEvaluationWizard(false);
    }
    // URL 변경 (뒤로가기 지원)
    window.history.pushState({}, '', '/evaluate');
    // 스크롤 상단으로 이동
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [useWizardMode]);

  // 평가 리셋 핸들러 (새 평가 시작)
  const handleReset = () => {
    setResult(null);
    setInputData(null);
    setError(null);
    setPrefillData(null);
    setIsDemoResult(false);
    // handleStartEvaluation을 재사용하여 평가 페이지로 이동
    handleStartEvaluation();
  };

  // 테스트케이스 관리 페이지 열기
  const handleOpenTestcaseManager = useCallback(() => {
    if (!ENABLE_TESTCASE_MANAGER) return;
    navigateWithGuard(() => {
      window.history.pushState({}, '', '/testcases');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }, [navigateWithGuard]);

  // 데모 결과 표시 핸들러
  const handleDemoSubmit = useCallback(() => {
    // 데모 입력 데이터에 [Sample] 접두사 추가
    const sampleInput = {
      ...demoInput,
      feature_name: `[Sample] ${demoInput.feature_name}`,
    };
    // 데모 결과 데이터에 [Sample] 접두사 추가
    const sampleResult = {
      ...demoResult,
      feature_name: `[Sample] ${demoResult.feature_name}`,
    };
    
    setInputData(sampleInput);
    setResult(sampleResult);
    setIsDemoResult(true);
    
    // GA4: 데모 평가 이벤트
    analytics.evaluateSuccess(demoResult.status, demoResult.confidence_score);
    
    // 스크롤 상단으로 이동
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [demoInput, demoResult]);

  // 히스토리 항목 선택 핸들러
  const handleSelectHistoryItem = useCallback((id: number) => {
    setSelectedHistoryId(id);
  }, []);

  // 히스토리 상세에서 다시 평가하기 핸들러
  const handleReEvaluate = useCallback((input: ReleaseInput) => {
    console.log('🔄 App handleReEvaluate called with:', input.feature_name);
    // handleReset()을 먼저 호출해서 결과/입력 상태 초기화
    // (handleReset이 setPrefillData(null)을 호출하므로 순서 중요)
    setResult(null);
    setInputData(null);
    setError(null);
    setIsDemoResult(false);
    
    // 그 다음 prefillData 설정
    setPrefillData(input);
    setSelectedHistoryId(null);
    setIsHistoryOpen(false);
    setShowHomePage(false);
    setShowDashboard(false);
    setShowEvaluationWizard(useWizardMode);
    window.history.pushState({}, '', '/evaluate');
    // popstate 이벤트 발생시켜 currentPath 상태 업데이트
    window.dispatchEvent(new PopStateEvent('popstate'));
    
    // 스크롤 상단으로 이동
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [useWizardMode]);

  const now = formatDate(new Date(), lang);

  // 랜덤 명언 선택 (페이지 로드 시 고정)
  const randomQuote = useMemo(() => {
    return TESTING_QUOTES[Math.floor(Math.random() * TESTING_QUOTES.length)];
  }, []);

  const quoteText = lang === 'ko' ? randomQuote.ko : randomQuote.en;

  // API 사용량 배너 표시 여부 계산 (80% 이상일 때)
  const showApiUsageBanner = useMemo(() => {
    if (!quota?.api_usage || quota.plan !== 'team') return false;
    const usagePercent = quota.api_usage.total_daily_limit > 0 
      ? (quota.api_usage.total_daily_usage / quota.api_usage.total_daily_limit) * 100 
      : 0;
    return usagePercent >= 80;
  }, [quota]);

  // 베타 종료 배너 표시 여부 계산 (Max 플랜 + 종료 7일 전부터)
  const showBetaEndingBanner = useMemo(() => {
    if (!isAuthenticated || quota?.plan !== 'max') return false;
    const now = new Date();
    // KST 기준: 2026-02-21 00:00:00 ~ 2026-02-28 23:59:59
    const bannerStartDate = new Date('2026-02-21T00:00:00+09:00');
    const betaEndDate = new Date('2026-02-28T23:59:59+09:00');
    return now >= bannerStartDate && now <= betaEndDate;
  }, [isAuthenticated, quota]);

  // 베타 종료 D-day 계산 (헤더에 표시)
  const betaDaysRemaining = useMemo(() => {
    const now = new Date();
    const betaEndDate = new Date('2026-02-28T23:59:59+09:00');
    if (now > betaEndDate) return null; // 베타 종료됨
    const diffTime = betaEndDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  // 상단 배너 표시 시 패딩 조정
  const topBannerCount = (showApiUsageBanner ? 1 : 0) + (showBetaEndingBanner ? 1 : 0);
  const topPadding = topBannerCount === 2 ? 'pt-24' : topBannerCount === 1 ? 'pt-12' : '';

  // ===== 조건부 라우팅 (모든 훅 호출 이후) =====
  
  // 에러 페이지 렌더링
  if (errorPageCode) {
    return <ErrorPage code={errorPageCode} />;
  }

  // 팀 초대 페이지 렌더링
  if (joinInviteToken) {
    return (
      <JoinTeamPage
        token={joinInviteToken}
        isLoggedIn={isAuthenticated}
        lang={lang}
      />
    );
  }

  // 히스토리 페이지 렌더링 (로그인 상태에서만)
  if (isHistoryPage && isAuthenticated) {
    return (
      <HistoryPage
        onReEvaluate={handleReEvaluate}
        onOpenPricing={() => setIsPricingOpen(true)}
      />
    );
  }

  // 테스트케이스 관리 페이지 렌더링
  if (isTestcaseManagerPage) {
    return (
      <div className={`min-h-screen py-6 px-4 sm:px-6 lg:px-8 ${topPadding}`} data-testid="testcase-manager-container">
        <BetaEndingBanner />
        <ApiUsageBanner />
        <div className="max-w-5xl mx-auto w-full">
          <TestcaseManagerPage
            onBack={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            onUseInEvaluation={(testcases) => {
              // 선택된 테스트케이스로 평가 시작 (향후 구현)
              console.log('Selected testcases for evaluation:', testcases);
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        </div>
      </div>
    );
  }

  // Flow Testcase Generator 페이지 렌더링
  if (isFlowGeneratorPage) {
    return (
      <div className={`min-h-screen py-6 px-4 sm:px-6 lg:px-8 ${topPadding}`} data-testid="flow-generator-container">
        <BetaEndingBanner />
        <ApiUsageBanner />
        <div className="max-w-5xl mx-auto w-full">
          <FlowTestcaseGeneratorPage
            onBack={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        </div>
      </div>
    );
  }

  // 비로그인 상태에서 히스토리 페이지 접근 시 - 기본 레이아웃에서 로그인 필요 메시지 표시
  const showLoginRequired = isHistoryPage && !isAuthenticated;
  const isWizardView = showEvaluationWizard && !result;

  return (
    <div className={`min-h-screen py-6 px-4 sm:px-6 lg:px-8 ${topPadding} ${(showLoginRequired || isWizardView) ? 'flex flex-col' : ''}`} data-testid="app-container">
      {/* 개발/스테이징 환경 전용 플랜 스위처 */}
      <DevPlanSwitcher />
      
      {/* 베타 종료 D-day 배너 (Max 플랜, 1/24~1/31 기간) */}
      <BetaEndingBanner />
      
      {/* API 사용량 경고 배너 (Team 플랜, 80% 이상 시 표시) */}
      <ApiUsageBanner />
      
      <div className={`max-w-3xl mx-auto w-full ${(showLoginRequired || isWizardView) ? 'flex flex-col flex-1' : ''}`}>
        {/* 상단 대시보드 - 네오 브루탈리즘 통합 */}
        <header className="mb-6 print:hidden" data-testid="app-header">
          <div className="neo-card-static bg-[var(--neo-lime)] dark:bg-[#1e2a3a]">
            {/* 상단: 브랜드 + 날짜 + 컨트롤 */}
            <div className="flex items-center justify-between p-3 border-b-[3px] border-black dark:border-[var(--neo-mint)]">
              {/* 브랜드 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateWithGuard(() => {
                    // 상태 리셋 (handleReset()은 /evaluate로 이동하므로 직접 리셋)
                    setResult(null);
                    setInputData(null);
                    setError(null);
                    setPrefillData(null);
                    setIsDemoResult(false);
                    // 홈으로 이동
                    setShowDashboard(false);
                    setShowHomePage(true);
                    setShowEvaluationWizard(false);
                    window.history.pushState({}, '', '/');
                  })}
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="w-8 h-8 bg-slate-900 dark:bg-slate-800 rounded flex items-center justify-center overflow-hidden">
                    <LogoAnimation size={32} loop={false} />
                  </div>
                  <h1 className="text-sm sm:text-base font-black text-black dark:text-[var(--neo-mint)] tracking-tight" data-testid="app-title">
                    {t.app.title}
                  </h1>
                </button>
                  <div className="flex items-center gap-1">
                    <span className="neo-badge bg-black text-[var(--neo-yellow)] dark:bg-[var(--neo-mint)] dark:text-black text-[9px] px-1.5 py-0.5">
                      BETA
                    </span>
                    <EnvironmentBadge />
                    <span className="text-[9px] font-bold text-black/50 dark:text-white/50">
                      ~{BETA_END_DATE.replace(/-/g, '.')}
                    </span>
                    {betaDaysRemaining !== null && betaDaysRemaining > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsBetaDdayInfoOpen(true);
                        }}
                        className={`neo-badge text-[9px] px-1.5 py-0.5 hover:opacity-90 transition-opacity ${
                          betaDaysRemaining <= 3 
                            ? 'bg-status-danger text-white animate-pulse' 
                            : betaDaysRemaining <= 7 
                              ? 'bg-[var(--neo-orange)] text-black' 
                              : 'bg-[var(--neo-pink)] text-black'
                        }`}
                        data-testid="beta-dday-badge"
                        aria-label={lang === 'ko' ? '베타 디데이 안내 보기' : 'View beta D-day info'}
                      >
                        D-{betaDaysRemaining}
                      </button>
                    )}
                  </div>
              </div>
              
              {/* 컨트롤 */}
              <div className="flex items-center gap-1 sm:gap-2">
                {isAuthenticated ? <UserMenu /> : <LoginButton />}
              </div>
            </div>
            
            {/* 중간: 플랜 정보 (로그인 시) - 클릭하면 아래에 대시보드 펼침/접힘 */}
            {isAuthenticated && quota && !result && !showDashboard && !showHomePage && (
              <>
                <button
                  onClick={() => {
                    analytics.dashboardClick();
                    setIsDashboardExpanded(prev => !prev);
                  }}
                  className={`w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-black/5 dark:bg-black/20 ${
                    isDashboardExpanded ? '' : 'border-b-[3px] border-black dark:border-[var(--neo-mint)]'
                  } hover:bg-black/10 dark:hover:bg-black/30 transition-colors cursor-pointer text-left`}
                >
                  <div className="flex items-center gap-3">
                    {/* 플랜 배지 */}
                    <span className={`neo-badge text-[10px] px-2 py-0.5 ${
                      quota.plan === 'team' 
                        ? 'bg-[var(--neo-yellow)] text-black' 
                        : quota.plan === 'pro' 
                          ? 'bg-[var(--neo-mint)] text-black' 
                          : 'bg-white text-black'
                    }`}>
                      {quota.plan.toUpperCase()}
                    </span>
                    
                    {/* 사용량 */}
                    <div className="flex items-center gap-1 text-xs font-bold text-black/80 dark:text-white">
                      {quota.monthly_evaluations.unlimited ? (
                        <span className="bg-black text-[var(--neo-lime)] dark:bg-[var(--neo-mint)] dark:text-black px-2 py-0.5 text-[10px]">
                          ∞ {lang === 'ko' ? '무제한' : 'UNLIMITED'}
                        </span>
                      ) : (
                        <span>
                          {quota.monthly_evaluations.used}/{quota.monthly_evaluations.limit}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* 기능 아이콘들 + 토글 화살표 */}
                  <div className="flex items-center gap-2 text-[10px] font-bold text-black/60 dark:text-white/70">
                    <span title={`${quota.limits.max_test_cases} test cases`}>📋{quota.limits.max_test_cases}</span>
                    <span title={`${quota.limits.max_planning_chars.toLocaleString()} chars`}>📝{Math.floor(quota.limits.max_planning_chars/1000)}K</span>
                    {quota.limits.history_limit > 0 && <span title="History">📚</span>}
                    {quota.limits.pdf_download && <span title="PDF">📄</span>}
                    <span className={`ml-1 text-black/40 dark:text-white/40 transition-transform ${isDashboardExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </div>
                </button>
                
                {/* 인라인 대시보드 (펼쳐졌을 때) */}
                {isDashboardExpanded && (
                  <InlineDashboard
                    onUpgrade={() => setIsPricingOpen(true)}
                    onClose={() => setIsDashboardExpanded(false)}
                  />
                )}
              </>
            )}
            
            {/* 하단: 네비게이션 */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              {/* 버전 + 날짜 */}
              <div className="flex items-center gap-2 text-[10px] font-bold text-black/50 dark:text-white/50">
                <span className="w-1.5 h-1.5 bg-[var(--neo-mint)]"></span>
                <span data-testid="app-version">{t.app.version}</span>
                <span>•</span>
                <span data-testid="app-date">{now}</span>
              </div>
              
            </div>
          </div>
        </header>

        {/* 메인 */}
        <main data-testid="app-main" className={showLoginRequired ? 'flex-1 flex items-center justify-center' : (isWizardView ? 'flex-1' : '')}>
          {showLoginRequired ? (
            /* 비로그인 상태에서 히스토리 페이지 접근 시 */
            <div className="neo-card-static bg-[#1e2a3a] p-8 text-center w-full">
              <h2 className="text-xl font-bold text-terminal-text mb-4">
                {t.auth.loginRequired}
              </h2>
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="neo-btn bg-white text-black px-6 py-3 mx-auto"
              >
                {t.dashboard.backToHome}
              </button>
            </div>
          ) : showDashboard ? (
            <Dashboard 
              onBack={() => { setShowDashboard(false); setShowHomePage(true); window.history.pushState({}, '', '/'); }} 
              onUpgrade={() => setIsPricingOpen(true)} 
            />
          ) : showHomePage && !result ? (
            /* 새로운 홈페이지 */
            <HomePage
              onStartEvaluation={handleStartEvaluation}
              onOpenPricing={() => setIsPricingOpen(true)}
              onOpenHistory={() => {
                analytics.historyClick();
                if (!isAuthenticated) {
                  setShowDemoSignupPopup(true);
                  return;
                }
                if (!quota) return;
                if (quota.limits.history_limit > 0) {
                  navigateWithGuard(() => {
                    window.history.pushState({}, '', '/history');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  });
                } else {
                  setIsPricingOpen(true);
                }
              }}
              onOpenHistoryItem={(recordId) => {
                analytics.historyClick();
                if (!isAuthenticated) {
                  setShowDemoSignupPopup(true);
                  return;
                }
                if (!quota) return;
                if (quota.limits.history_limit > 0) {
                  navigateWithGuard(() => {
                    setIsHistoryOpen(false);
                    setSelectedHistoryId(recordId);
                  });
                } else {
                  setIsPricingOpen(true);
                }
              }}
              onOpenDashboard={() => {
                if (!isAuthenticated) {
                  setShowDemoSignupPopup(true);
                  return;
                }
                navigateWithGuard(() => {
                  analytics.dashboardClick();
                  setShowHomePage(false);
                  setShowDashboard(true);
                  window.history.pushState({}, '', '/dashboard');
                });
              }}
              onOpenStatistics={() => setIsStatisticsModalOpen(true)}
              onOpenTestcaseAnalytics={() => setIsTestcaseAnalyticsOpen(true)}
              onOpenCriteriaSettings={() => setIsCriteriaSettingsOpen(true)}
              onOpenTeamDashboard={() => setIsTeamDashboardOpen(true)}
              onOpenTestcaseManager={handleOpenTestcaseManager}
              onDemoSubmit={handleDemoSubmit}
              onOpenProfile={() => {
                analytics.profileClick();
                setIsProfileOpen(true);
              }}
            />
          ) : showEvaluationWizard && !result ? (
            /* 스텝 기반 위자드 모드 */
            <EvaluationWizard
              onSubmit={handleSubmit}
              isLoading={isLoading}
              prefillData={isDemo ? demoInput : prefillData}
              isDemo={isDemo}
              onDemoSubmit={handleDemoSubmit}
              onRequestOpenProfile={() => setIsProfileOpen(true)}
              onCancel={() => {
                setShowEvaluationWizard(false);
                setShowHomePage(true);
                window.history.pushState({}, '', '/');
              }}
            />
          ) : !result ? (
            /* 기존 폼 모드 */
            <ReleaseForm 
              onSubmit={handleSubmit} 
              isLoading={isLoading} 
              prefillData={isDemo ? demoInput : prefillData}
              isDemo={isDemo}
              onDemoSubmit={handleDemoSubmit}
              onUnsavedChangesChange={setHasUnsavedChanges}
              onRequestOpenProfile={() => setIsProfileOpen(true)}
            />
          ) : (
            <div>
              {/* 데모 배너 */}
              {isDemoResult && (
                <div className="mb-4 p-3 bg-[var(--neo-mint)]/10 border-2 border-[var(--neo-mint)] rounded-lg relative z-20">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[var(--neo-mint)] font-bold">✨ {t.demo.resultBanner}</span>
                    <span className="text-terminal-muted">{t.demo.resultDescription}</span>
                  </div>
                </div>
              )}
              {/* 워터마크는 ResultBoard 내부에서 처리 (PDF 캡처 시 포함됨) */}
              <ResultBoard result={result} inputData={inputData} onReset={handleReset} isLoading={isLoading} isDemo={isDemoResult} />
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-status-danger text-sm font-mono" data-testid="error-message">
              ERROR: {error}
            </div>
          )}
        </main>

        {/* 푸터 - 네오 브루탈리즘 */}
        <footer className={`${isWizardView ? 'mt-auto pt-4' : 'mt-10'} print:hidden`} data-testid="app-footer">
          {/* 명언 카드 */}
          <div className="neo-card-static bg-[var(--neo-pink)] dark:bg-[#2d3a4f] dark:border-[var(--neo-pink)] p-4 mb-4">
            <p className="text-black dark:text-white text-sm font-bold" data-testid="footer-quote">
              "{quoteText}"
            </p>
            <p className="text-black/60 dark:text-white/60 text-xs font-bold mt-2 uppercase" data-testid="footer-author">
              — {randomQuote.author}
            </p>
          </div>
          
          {/* 브랜드 바 */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <div className="flex items-center gap-2 text-terminal-text text-xs font-bold">
              <span className="w-3 h-3 bg-[var(--neo-mint)]"></span>
              <span className="uppercase tracking-wider">ReleaseGate</span>
              <span className="text-terminal-dim">•</span>
              <span className="text-terminal-muted">{t.footer.tagline}</span>
            </div>
            <div className="text-terminal-dim text-xs font-medium">
              {t.footer.copyright}
            </div>
          </div>
        </footer>
      </div>

      {/* Pricing 모달 */}
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />

      {/* History 모달 */}
      <HistoryModal 
        isOpen={isHistoryOpen} 
        onClose={() => setIsHistoryOpen(false)} 
        onSelectItem={handleSelectHistoryItem}
        quota={quota}
        onUpgrade={() => {
          setIsHistoryOpen(false);
          setIsPricingOpen(true);
        }}
      />

      {/* History Detail 모달 */}
      <HistoryDetailModal
        isOpen={selectedHistoryId !== null}
        recordId={selectedHistoryId}
        onClose={() => setSelectedHistoryId(null)}
        onReEvaluate={handleReEvaluate}
        onOpenPricing={() => setIsPricingOpen(true)}
      />

      {/* 평가 통계 모달 */}
      <StatisticsModal
        isOpen={isStatisticsModalOpen}
        onClose={() => setIsStatisticsModalOpen(false)}
      />

      {/* 테스트케이스 분석 모달 */}
      <TestcaseAnalytics
        isOpen={isTestcaseAnalyticsOpen}
        onClose={() => setIsTestcaseAnalyticsOpen(false)}
      />

      {/* 릴리즈 판정 기준 설정 모달 */}
      <CriteriaSettingsModal
        isOpen={isCriteriaSettingsOpen}
        onClose={() => setIsCriteriaSettingsOpen(false)}
      />

      {/* 팀 대시보드 모달 */}
      <TeamDashboardModal
        isOpen={isTeamDashboardOpen}
        onClose={() => setIsTeamDashboardOpen(false)}
      />

      {/* 데모 회원가입 유도 팝업 */}
      {showDemoSignupPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="neo-card-static bg-white dark:bg-[#1a1a2e] max-w-md w-full p-6 animate-in zoom-in-95 fade-in duration-300">
            {/* 헤더 */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[var(--neo-mint)] mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">🚀</span>
              </div>
              <h3 className="text-xl font-black text-black dark:text-white mb-2">
                {lang === 'ko' ? '실제 AI 평가를 받아보세요!' : 'Get Real AI Evaluation!'}
              </h3>
              <p className="text-sm text-terminal-muted">
                {lang === 'ko' 
                  ? '지금 보신 건 데모 결과예요. 로그인하면 직접 입력한 데이터로 AI가 분석해드려요!'
                  : 'What you just saw was a demo. Login to get AI analysis on your own data!'}
              </p>
            </div>

            {/* 혜택 리스트 */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 bg-[var(--neo-lime)] flex items-center justify-center text-xs font-bold text-black">✓</span>
                <span className="text-terminal-text">{lang === 'ko' ? '무료로 월 3회 평가' : '3 free evaluations per month'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 bg-[var(--neo-lime)] flex items-center justify-center text-xs font-bold text-black">✓</span>
                <span className="text-terminal-text">{lang === 'ko' ? '실시간 AI 릴리즈 분석' : 'Real-time AI release analysis'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 bg-[var(--neo-lime)] flex items-center justify-center text-xs font-bold text-black">✓</span>
                <span className="text-terminal-text">{lang === 'ko' ? '구조화된 권고사항 제공' : 'Structured recommendations'}</span>
              </div>
            </div>

            {/* 버튼 */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  analytics.demoSignupClick();
                  setShowDemoSignupPopup(false);
                  // LoginButton 클릭 트리거를 위해 document에서 찾기
                  const loginBtn = document.querySelector('[data-testid="login-button"]') as HTMLButtonElement;
                  if (loginBtn) loginBtn.click();
                }}
                className="w-full py-3 neo-btn bg-[var(--neo-mint)] text-black font-bold text-sm"
              >
                {lang === 'ko' ? '🔐 Google로 무료 시작하기' : '🔐 Start Free with Google'}
              </button>
              <button
                onClick={() => setShowDemoSignupPopup(false)}
                className="w-full py-2 text-sm text-terminal-muted hover:text-terminal-text transition-colors"
              >
                {lang === 'ko' ? '나중에 할게요' : 'Maybe later'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 변경사항 이탈 확인 팝업 - 폼 작성 중(결과가 없을 때)에만 표시 */}
      {pendingNavigation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="neo-card-static bg-white dark:bg-[#1a1a2e] max-w-md w-full p-6 animate-in zoom-in-95 fade-in duration-300">
            {/* 헤더 */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[var(--neo-orange)] mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">⚠️</span>
              </div>
              <h3 className="text-xl font-black text-black dark:text-white mb-2">
                {t.form.unsavedChangesTitle}
              </h3>
              <p className="text-sm text-terminal-muted">
                {t.form.unsavedChangesMessage}
              </p>
            </div>

            {/* 오늘 하루 묻지 않기 체크박스 */}
            <label className="flex items-center gap-2 mb-4 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontAskTodayChecked}
                onChange={(e) => setDontAskTodayChecked(e.target.checked)}
                className="w-4 h-4 rounded border-2 border-terminal-border bg-transparent checked:bg-[var(--neo-mint)] checked:border-[var(--neo-mint)] focus:ring-2 focus:ring-[var(--neo-mint)] focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-terminal-muted group-hover:text-terminal-text transition-colors">
                {t.form.unsavedChangesDontAskToday}
              </span>
            </label>

            {/* 버튼 */}
            <div className="space-y-3">
              <button
                onClick={handleCancelLeave}
                className="w-full py-3 neo-btn bg-[var(--neo-mint)] text-black font-bold text-sm"
              >
                {t.form.unsavedChangesStay}
              </button>
              <button
                onClick={handleConfirmLeave}
                className="w-full py-2 text-sm text-terminal-muted hover:text-status-danger transition-colors border border-terminal-border rounded hover:border-status-danger"
              >
                {t.form.unsavedChangesLeave}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 온보딩 모달 (Feature Onboarding - 4단계) */}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={handleOnboardingClose} 
      />

      {/* Welcome 온보딩 (최초 접근자용 - 서비스 소개) */}
      <WelcomeOnboarding
        isOpen={showWelcomeOnboarding}
        onClose={handleWelcomeComplete}
        onJustBrowse={handleWelcomeComplete}
      />

      {/* GitHub 로그인/회원가입(약관 동의) 모달 */}
      <TermsModal
        isOpen={!!githubPreauth}
        onClose={clearGithubPreauth}
        onAgree={async (terms) => {
          if (!githubPreauth) return;
          setIsGithubRegistering(true);
          try {
            await completeGithubRegister(githubPreauth.preauthToken, terms);
            clearGithubPreauth();
          } catch (e) {
            console.error('[GitHub] Registration failed:', e);
          } finally {
            setIsGithubRegistering(false);
          }
        }}
        userInfo={githubPreauth ? { name: githubPreauth.name, email: githubPreauth.email, picture: githubPreauth.picture ?? null } : null}
        isLoading={isGithubRegistering}
      />

      {/* 로그인 처리 중 딤 오버레이 */}
      {isLoggingIn && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            {/* 스피너 */}
            <div className="w-12 h-12 border-4 border-status-go/30 border-t-status-go rounded-full animate-spin" />
            {/* 메시지 */}
            <p className="text-lg font-medium text-white">{t.auth.loggingIn}</p>
          </div>
        </div>
      )}

      {/* 앱 초기화(인증 확인) 중 딤 오버레이 */}
      {isAuthLoading && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            {/* 스피너 */}
            <div className="w-12 h-12 border-4 border-status-go/30 border-t-status-go rounded-full animate-spin" />
          </div>
        </div>
      )}

      {/* 자동 로그아웃 경고 모달 (10분 카운트다운) */}
      <AutoLogoutWarning
        isOpen={autoLogout.showWarning}
        remainingTime={autoLogout.remainingTime}
        onExtend={autoLogout.extendSession}
        onLogout={autoLogout.logoutNow}
      />

      {/* 자동 로그아웃 완료 알림 모달 */}
      <LoggedOutModal
        isOpen={autoLogout.showLoggedOutModal}
        onClose={autoLogout.closeLoggedOutModal}
        onLogin={() => {
          autoLogout.closeLoggedOutModal();
          // LoginButton 클릭 트리거
          const loginBtn = document.querySelector('[data-testid="login-button"]') as HTMLButtonElement;
          if (loginBtn) loginBtn.click();
        }}
      />

      {/* 프로필 모달 (홈페이지 인사말 클릭 시) */}
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />

      {/* 탈퇴 완료 안내 모달 */}
      <FarewellModal
        isOpen={showFarewellModal}
        onClose={() => {
          setShowFarewellModal(false);
          setFarewellPayload(null);
        }}
        payload={farewellPayload}
      />

      {/* 계정 통합 후 보안 로그아웃 안내 모달 */}
      {showMergeLogoutNotice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="neo-card-static bg-white dark:bg-[#1a1a2e] max-w-md w-full p-6 animate-in zoom-in-95 fade-in duration-300">
            <div className="text-center mb-5">
              <div className="w-16 h-16 bg-[var(--neo-mint)] mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">🧩</span>
              </div>
              <h3 className="text-xl font-black text-black dark:text-white mb-2">
                {lang === 'ko' ? '계정 통합 완료' : 'Account merge completed'}
              </h3>
              <p className="text-sm text-terminal-muted">
                {lang === 'ko'
                  ? '보안을 위해 자동 로그인하지 않고 로그아웃 처리했어요. 통합된 계정으로 다시 로그인해주세요.'
                  : 'For security, you were logged out. Please log in again to the merged account.'}
              </p>
              {mergeLogoutNoticeEmail && (
                <p className="text-xs text-terminal-muted mt-2">
                  {lang === 'ko' ? `통합된 계정: ${mergeLogoutNoticeEmail}` : `Merged account: ${mergeLogoutNoticeEmail}`}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowMergeLogoutNotice(false);
                  // LoginButton 클릭 트리거
                  const loginBtn = document.querySelector('[data-testid="login-button"]') as HTMLButtonElement;
                  if (loginBtn) loginBtn.click();
                }}
                className="w-full py-3 neo-btn bg-[var(--neo-mint)] text-black font-bold text-sm"
              >
                {lang === 'ko' ? '다시 로그인' : 'Log in again'}
              </button>
              <button
                onClick={() => setShowMergeLogoutNotice(false)}
                className="w-full py-2 text-sm text-terminal-muted hover:text-terminal-text transition-colors"
              >
                {lang === 'ko' ? '닫기' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 베타 D-day 안내 모달 */}
      {isBetaDdayInfoOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setIsBetaDdayInfoOpen(false)}
          data-testid="beta-dday-modal"
        >
          <div
            className="neo-card-static bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-black text-terminal-text">
                  {lang === 'ko' ? 'D-day 안내' : 'D-day Info'}
                </h3>
                <p className="text-xs text-terminal-muted mt-1">
                  {lang === 'ko'
                    ? `기준일: ${BETA_END_DATE.replace(/-/g, '.')} (KST 기준)`
                    : `Ends on: ${BETA_END_DATE.replace(/-/g, '.')} (KST)`}
                </p>
              </div>
              <button
                onClick={() => setIsBetaDdayInfoOpen(false)}
                className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
                aria-label={lang === 'ko' ? '닫기' : 'Close'}
              >
                <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3 text-sm text-terminal-text">
              <div className="bg-terminal-dim/20 border border-terminal-border rounded-lg p-3">
                <p className="font-semibold mb-1">
                  {lang === 'ko' ? 'D-XX는 무엇인가요?' : 'What does D-XX mean?'}
                </p>
                <p className="text-xs text-terminal-muted leading-relaxed">
                  {lang === 'ko'
                    ? '베타 종료(기준일)까지 남은 날짜입니다. 출시 전에 서비스 사용성을 충분히 경험할 수 있도록 무료로 제공하는 기간을 의미해요.'
                    : 'It’s the number of days left until the beta ends. It represents a free period to experience the product before launch.'}
                </p>
              </div>

              <div className="bg-terminal-dim/20 border border-terminal-border rounded-lg p-3">
                <p className="font-semibold mb-1">
                  {lang === 'ko' ? '왜 보여주나요?' : 'Why show this?'}
                </p>
                <ul className="text-xs text-terminal-muted leading-relaxed list-disc pl-4 space-y-1">
                  <li>
                    {lang === 'ko'
                      ? '출시 전: 기능/UX를 빠르게 다듬기 위한 베타 운영'
                      : 'Pre-launch beta to rapidly improve features and UX'}
                  </li>
                  <li>
                    {lang === 'ko'
                      ? '피드백 기반으로 더 좋은 “릴리즈 Go/No-Go” 경험을 만들기 위함'
                      : 'To build a better “Release Go/No-Go” experience with feedback'}
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setIsBetaDdayInfoOpen(false)}
                className="neo-btn bg-[var(--neo-mint)] text-black px-5 py-2 text-sm"
              >
                {lang === 'ko' ? '확인' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
